import { Send, Square } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

interface AnimatedSendButtonProps {
  onClick: () => void;
  disabled: boolean;
  isLoading: boolean;
  /** Mini composer keeps the tenant primary fill even while the empty field disables sending. */
  retainPrimaryFill?: boolean;
}

export const AnimatedSendButton = ({
  onClick,
  disabled,
  isLoading,
  retainPrimaryFill = false,
}: AnimatedSendButtonProps) => {
  const isTrulyDisabled = disabled && !isLoading;
  const reducedMotion = useReducedMotion();

  const icon = isLoading ? (
    <Square className="size-3.5 shrink-0 fill-current" />
  ) : (
    <Send className="size-4 shrink-0 -translate-x-[1px]" />
  );

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full p-2 outline-none ring-ring transition-colors focus-visible:ring-2 disabled:cursor-not-allowed ${
        isLoading
          ? 'bg-primary text-primary-foreground hover:bg-primary/90'
          : isTrulyDisabled && !retainPrimaryFill
            ? 'bg-muted text-muted-foreground'
            : 'bg-primary text-primary-foreground hover:bg-primary/90'
      }`}
      aria-label={isLoading ? 'Stop generating response' : 'Send message'}
    >
      {reducedMotion ? (
        icon
      ) : (
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={isLoading ? 'stop' : 'send'}
            className="flex items-center justify-center"
            initial={{ scale: 0.5, rotate: -90, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            exit={{ scale: 0.5, rotate: 90, opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
            {icon}
          </motion.span>
        </AnimatePresence>
      )}
    </button>
  );
};
